// TextureWizard — drop a PNG/JPG → ask for a name + folder → POST
// /api/characters/{slug}/textures. Slot-less; textures are first-class
// asset_rows now and get wired into materials via the MaterialInspector
// (drag a texture card onto a material slot field) rather than directly
// onto a role.

import React, { useEffect, useRef, useState } from 'react'
import WizardShell, { inputStyle, labelStyle, tokens } from './WizardShell'
import { getCurrentCharacterId } from '../../../lib/characterState'
import { CustomAssetManager } from '../../../lib/CustomAssetManager'

function baseName(file) {
  return (file?.name ?? '').replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_\-]+/g, '_')
}

export default function TextureWizard({ file, path = [], onDone, onCancel }) {
  const [name, setName] = useState(baseName(file))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [imgDims, setImgDims] = useState(null)
  const previewRef = useRef(null)

  useEffect(() => {
    if (!file) return
    const url = URL.createObjectURL(file)
    if (previewRef.current) previewRef.current.src = url
    const img = new Image()
    img.onload = () => setImgDims({ w: img.naturalWidth, h: img.naturalHeight })
    img.src = url
    return () => URL.revokeObjectURL(url)
  }, [file])

  const submit = async () => {
    if (!file) { setError('No file'); return }
    if (!getCurrentCharacterId()) { setError('No active character'); return }
    setSubmitting(true); setError(null)
    try {
      const folderPath = path.length ? '/' + path.join('/') : '/textures'
      const row = await CustomAssetManager.uploadTextureAsset(file, {
        name: name.trim(),
        folderPath,
      })
      onDone?.({ id: row.id, url: row.file_url })
    } catch (e) {
      setError(String(e.message ?? e))
      setSubmitting(false)
    }
  }

  return (
    <WizardShell
      title="New texture"
      subtitle={file?.name}
      onCancel={onCancel}
      onSubmit={submit}
      submitDisabled={submitting || !name.trim()}
      submitLabel={submitting ? 'Uploading…' : 'Upload'}
      error={error}
    >
      <label style={labelStyle}>Name</label>
      <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} autoFocus />

      <label style={labelStyle}>Preview</label>
      <div style={{
        background: tokens.bgInset, border: `1px solid ${tokens.border}`, borderRadius: 4,
        padding: 8, display: 'flex', justifyContent: 'center', alignItems: 'center',
        minHeight: 100,
      }}>
        <img ref={previewRef} alt="" style={{ maxWidth: '100%', maxHeight: 200, display: 'block' }} />
      </div>
      <div style={{ marginTop: 6, fontFamily: 'var(--m-mono)', fontSize: 9.5, color: tokens.dim }}>
        {imgDims ? `${imgDims.w} × ${imgDims.h}` : 'loading…'}
        {' · '}folder <code>{path.length ? '/' + path.join('/') : '/textures'}</code>
      </div>
    </WizardShell>
  )
}
